import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { EmployeeService } from '../../../employees/services/employee.service';
import {debounceTime, filter, map, startWith, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import { SearchEmployeeData } from '../../../employees/domain/search-employee-data';
import { AssignEmployeeService } from '../../../services/assign-employee.service';
import { Profession } from '../../../employees/domain/profession';
import {BehaviorSubject, combineLatestWith, Subject} from 'rxjs';
import { EmployeeConflict } from '../../models/employee-conflict';
import { PhaseWithEmployees } from '../../../projects/domain/phase-with-employees';
import { EmployeeListItem } from '../../../employees/domain/employee-list';
import { RatingForm } from '../../../projects/domain/employee-assessment-form';
import { EmployeeWarningStatus } from "../../employee-conflicts-tooltip/employee-conflicts-tooltip.component";
import { MatSelectionListChange } from "@angular/material/list";
import {CertificateNameService} from "../../../../core/certificate-name.service";
import {SkillGroupService} from "../../../employees/services/skill-group.service";
import {StatusesService} from "../../../employees/services/statuses.service";
import {VisaTypeService} from "../../../services/visa-type.service";

interface EmployeeFiltersForm {
  search: FormControl<string>;
  availableOnly: FormControl<boolean>;
  professionId: FormControl<number | null>;
  ratings: FormArray<FormControl<number>>;
  statuses: FormControl<string[]>;
  skillIds: FormControl<number[]>;
  certificateIds: FormControl<number[]>;
  visaTypeIds: FormControl<number[]>;
}

interface EmployeeFiltersValue {
  search: string;
  availableOnly: boolean;
  professionId: number | null;
  ratings: Array<number | null>;
  statuses: string[];
  skillIds: number[];
  certificateIds: number[];
  visaTypeIds: number[];
}

interface Conflicts {
  employeeId: string;
  conflicts: EmployeeConflict[];
}

@Component({
  selector: 'app-recommend-employees-step',
  templateUrl: 'recommend-employees-step.component.html',
  styleUrls: ['../employee-recommend.scss'],
  standalone: false,
})
export class RecommendEmployeesStepComponent implements OnInit, OnDestroy {
  @Input() isPreviousStep: boolean;
  @Input() professions: Profession[];
  @Input() projectId: string;
  @Input() phase: PhaseWithEmployees;
  @Input() recommendation: PhaseWithEmployees;

  @Output() closeEmployeeRecommendation = new EventEmitter<void>();
  @Output() backToPreviousStep = new EventEmitter<void>();
  @Output() employeeSelected = new EventEmitter<string[]>();

  employeeFilterForm: FormGroup<EmployeeFiltersForm> = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
    availableOnly: new FormControl<boolean>(false, { nonNullable: true }),
    professionId: new FormControl<number | null>(null),
    ratings: new FormArray<FormControl<number>>([]),
    statuses: new FormControl<string[]>([]),
    skillIds: new FormControl<number[]>([]),
    certificateIds: new FormControl<number[]>([]),
    visaTypeIds: new FormControl<number[]>([]),
  });

  employeeForm = new FormGroup({
    employeeId: new FormControl<string[]>([], Validators.required),
  });
  private selectedEmployeeIds = new Set<string>();

  isConflictsPopoverOpen = false;
  currentPopoverWarningStatus: EmployeeWarningStatus | null = null;
  hoveredEmployeeId: string | null = null;
  hoveredProfessionId: number | null = null;
  private tooltipCloseTimeout: any;
  private lastEmployeeId: Conflicts[] = [];

  private currentEmployees: EmployeeListItem[] = [];

  readonly skillGroups$ = this.skillGroupService.skillGroupsAllWithRemoved$.pipe(
    map(skillGroups => skillGroups
      .filter(sg => sg.skills?.length)
      .map(sg => ({
        ...sg,
        name: sg.isRemoved ? `${sg.name} (Removed)` : sg.name,
        skills: sg.skills.map(skill => ({
          ...skill,
          name: skill.isRemoved ? `${skill.name} (Removed)` : skill.name
        }))
      }))
    )
  );

  readonly statuses$ = this.statusesService.statuses$;

  readonly certificates$ = this.certificateService.allWithRemovedCertificates$.pipe(
    map(certs => certs.map(cert => ({
      ...cert,
      name: cert.isRemoved ? `${cert.name} (Removed)` : cert.name
    })))
  );
  readonly visaTypes$ = this.visaTypeService.allWithRemovedOrUsedVisas$.pipe(
    map(visaTypes => visaTypes.map(visaType => ({
      ...visaType,
      name: visaType.isRemoved ? `${visaType.name} (Removed)` : visaType.name
    })))
  );

  readonly employees$ = this.employeeFilterForm.valueChanges.pipe(
    debounceTime(300),
    filter(() => this.employeeFilterForm.valid),
    startWith({ search: '', ratings: [], statuses: [], skillIds: [], certificateIds: [], visaTypeIds: [] }),
    combineLatestWith([]),
    map(([filterFormValue]: [Partial<EmployeeFiltersValue>, any[]]): [Partial<EmployeeFiltersValue>, Partial<SearchEmployeeData>] =>
      [filterFormValue, this.mapToSearchEmployeeData(filterFormValue)],
    ),
    switchMap(([filterFormValue, filters]) =>
      this.employeeService.getEmployeeList(filters).pipe(
        map((res) => res.items),
        map((employees) =>
          filterFormValue.availableOnly
            ? employees.filter((emp) => emp.isAvailable && !this.checkAlreadyRecommended(emp.id))
            : employees
        ),
        map((employees) => employees.filter((emp) => emp.status.status !== 'Blocked')),
        tap((employees) => {
          const assignedOrRecommended = employees.filter(
            (e) => this.checkAlreadyAssigned(e.id) || this.checkAlreadyRecommended(e.id)
          );
          assignedOrRecommended.forEach((emp) => this.selectedEmployeeIds.add(emp.id));
        })
      )
    ),
    tap(() => this.conflicts$.next([]))
  );

  readonly conflicts$ = new BehaviorSubject<EmployeeConflict[]>([]);
  private destroy$ = new Subject<void>();
  constructor(
    private readonly employeeService: EmployeeService,
    private readonly assignEmployeeService: AssignEmployeeService,
    private certificateService: CertificateNameService,
    private skillGroupService: SkillGroupService,
    private statusesService: StatusesService,
    private visaTypeService: VisaTypeService,
  ) {}

  ngOnInit() {
    this.employeeFilterForm.controls.professionId.valueChanges.pipe(
      tap((professionId) => {
        if (professionId) {
          const requiredProfession = this.phase.requiredProfessions?.find(
            rp => rp.professionId === professionId
          );
          if (requiredProfession) {
            const certificateIds = requiredProfession.requiredCertificates?.map(c => c.id) || [];
            const skillIds = requiredProfession.requiredSkills?.map(s => s.id) || [];
            this.employeeFilterForm.patchValue({ certificateIds, skillIds }, { emitEvent: false });
          }
        } else {
          this.employeeFilterForm.patchValue({ certificateIds: [], skillIds: [] }, { emitEvent: false });
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.employees$
      .pipe(takeUntil(this.destroy$))
      .subscribe((employees) => {
      this.currentEmployees = employees;
      const validSelected = employees
        .map((e) => e.id)
        .filter((id) => this.selectedEmployeeIds.has(id));
      this.employeeForm.patchValue({ employeeId: validSelected });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onEmployeeSelectionChange(event: MatSelectionListChange) {
    const option = event.options[0];
    const empId = option.value as string;

    if (option.selected) {
      this.selectedEmployeeIds.add(empId);
    } else {
      this.selectedEmployeeIds.delete(empId);
    }

    this.employeeForm.patchValue({
      employeeId: Array.from(this.selectedEmployeeIds),
    });
  }

  onEmployeeSelect(): void {
    const selectedEmployeeIds = Array.from(this.selectedEmployeeIds);
    const manualSelection = selectedEmployeeIds.filter(
      (id) => !this.checkAlreadyAssigned(id) && !this.checkAlreadyRecommended(id)
    );

    this.employeeSelected.emit(manualSelection);
  }

  openConflictsPopover(employeeId: string) {
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
      this.tooltipCloseTimeout = null;
    }

    const employee = this.currentEmployees.find((emp) => emp.id === employeeId);
    if (employee?.warningStatus) {
      this.currentPopoverWarningStatus = {
        status: employee.status.status,
        expirationDate: employee.status.expirationDate,
      };
    } else {
      this.currentPopoverWarningStatus = null;
    }

    this.hoveredEmployeeId = employeeId;
    this.isConflictsPopoverOpen = true;

    const existingEntry = this.lastEmployeeId.find((entry) => entry.employeeId === employeeId);
    if (existingEntry) {
      this.conflicts$.next(existingEntry.conflicts);
    } else {
      this.assignEmployeeService
        .checkConflictsWithProject(this.projectId, this.phase.id, [employeeId]).pipe(take(1))
        .subscribe({
          next: (res) => {
            this.lastEmployeeId.push({ employeeId, conflicts: res });
            this.conflicts$.next(res);
          },
          error: (err) => {
            const conflicts = err?.error || [];
            this.lastEmployeeId.push({ employeeId, conflicts });
            this.conflicts$.next(conflicts);
          },
        });
    }
  }

  closeConflictsPopover() {
    this.tooltipCloseTimeout = setTimeout(() => {
      this.hoveredEmployeeId = null;
      this.isConflictsPopoverOpen = false;
    }, 100);
  }

  cancelClosingPopover() {
    if (this.tooltipCloseTimeout) {
      clearTimeout(this.tooltipCloseTimeout);
      this.tooltipCloseTimeout = null;
    }
  }

  onProfessionMouseEnter(professionId: number): void {
    this.hoveredProfessionId = professionId;
  }
  onProfessionMouseLeave(): void {
    this.hoveredProfessionId = null;
  }

  findRequiredSkillsOrCertificatesByProfessionId(
    professionId: number,
    requiredType: 'skills' | 'certificates'
  ): any[] {
    if (!this.phase || !this.phase.requiredProfessions) {
      return [];
    }
    const requiredProfession = this.phase.requiredProfessions.find(rp => rp.professionId === professionId);
    if (!requiredProfession) {
      return [];
    }
    if ('skills' === requiredType && requiredProfession.requiredSkills) {
      return requiredProfession.requiredSkills;
    }
    if ('certificates' === requiredType && requiredProfession.requiredCertificates) {
      return requiredProfession.requiredCertificates;
    }
    return [];
  }

  onEmployeeRecommendClose(): void {
    this.closeEmployeeRecommendation.emit();
  }

  goBackToPreviousStep(): void {
    this.backToPreviousStep.emit();
  }

  checkAlreadyAssigned(employeeId: string): boolean {
    return this.phase?.employees?.some((emp) => emp.employeeId === employeeId) || false;
  }

  checkAlreadyRecommended(employeeId: string): boolean {
    return this.recommendation?.employees?.some((emp) => emp.employeeId === employeeId) || false;
  }

  isRecommendDisabled(): boolean {
    const selectedIds = this.employeeForm.getRawValue().employeeId || [];
    return !selectedIds.length || selectedIds.every(
      (id) => this.checkAlreadyAssigned(id) || this.checkAlreadyRecommended(id)
    );
  }

  employeeIdTracker(index: number, item: EmployeeListItem): string {
    return item.id;
  }
  professionIdTracker(index: number, item: Profession): number {
    return item.id;
  }

  get filteredProfessions(): Profession[] {
    if (this.phase && this.phase.requiredProfessions?.length) {
      const requiredIds = this.phase.requiredProfessions
        .map(rp => rp.professionId);
      return this.professions?.filter(p => requiredIds.includes(p.id));
    }

    return this.professions;
  }

  private mapToSearchEmployeeData(
    filterFormValue: Partial<EmployeeFiltersValue>
  ): Partial<SearchEmployeeData> {
    const mappedRatings: RatingForm[] = (filterFormValue.ratings || []).map(
      (rating, index) => ({
        value: rating,
        categoryId: index,
      })
    );
    return {
      pageIndex: 0,
      pageSize: 8,
      search: filterFormValue.search || null,
      professionId: filterFormValue.professionId,
      statuses: filterFormValue.statuses,
      skillIds: filterFormValue.skillIds,
      certificateIds: filterFormValue.certificateIds,
      visaTypeIds: filterFormValue.visaTypeIds,
      availableFrom: this.phase?.startDate,
      availableTo: this.phase?.endDate,
      ratings: mappedRatings,
    };
  }
}
